import { create } from 'zustand';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastItem {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
  durationMs: number;
  action?: ToastAction;
}

export type ToastInput = Omit<ToastItem, 'id' | 'durationMs'> & { durationMs?: number };

interface NotificationsState {
  toasts: ToastItem[];
  push: (input: ToastInput) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

const DEFAULT_DURATION_MS = 4500;

const genId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  toasts: [],
  push: (input) => {
    const id = genId();
    const item: ToastItem = {
      id,
      variant: input.variant,
      title: input.title,
      description: input.description,
      durationMs: input.durationMs ?? DEFAULT_DURATION_MS,
      action: input.action,
    };
    set((s) => ({ toasts: [...s.toasts, item] }));
    if (item.durationMs > 0) {
      setTimeout(() => get().dismiss(id), item.durationMs);
    }
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}));

// Imperative helper for non-React contexts (Axios interceptors, etc.)
export const notify = {
  success: (title: string, description?: string) =>
    useNotificationsStore.getState().push({ variant: 'success', title, description }),
  error: (title: string, description?: string) =>
    useNotificationsStore.getState().push({ variant: 'error', title, description }),
  warning: (title: string, description?: string) =>
    useNotificationsStore.getState().push({ variant: 'warning', title, description }),
  info: (title: string, description?: string) =>
    useNotificationsStore.getState().push({ variant: 'info', title, description }),
};
